/**
 * Bottom-left table chat + action/card log (PokerStars-style).
 * Remote WS chat_message lines merge into shared chat tab.
 */
import { useEffect, useRef, useState } from "react";
import type {
  ChatMessageEvent,
  LastAction,
} from "@/hooks/use-poker-websocket";

export type ChatLine = {
  id: string;
  kind: "system" | "action" | "cards" | "chat";
  text: string;
  at: number;
};

export function TableChatLog(props: {
  tableId: string;
  lastAction: LastAction | null;
  /** Remote + local shared chat from WS hook */
  chatMessages?: ChatMessageEvent[];
  /** Local wallet for "You" label */
  selfWallet?: string | null;
  communityCards?: Array<{ rank: string; suit: string }>;
  phase?: string;
  handResult?: string | null;
  sendChat?: (text: string) => void;
  connected?: boolean;
  /** Relay chat to Vidu BUDB head — HU VS BUDB only */
  budbVoice?: boolean;
}) {
  const [tab, setTab] = useState<"chat" | "log">("chat");
  const [logLines, setLogLines] = useState<ChatLine[]>(() => [
    {
      id: "welcome",
      kind: "system",
      text: props.budbVoice
        ? "HU VS BUDB · chat + live voice with BetUDontBet (agent seat)"
        : "Table chat · live · log = actions & board · no voice on cash tables",
      at: Date.now(),
    },
  ]);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastActRef = useRef<string>("");
  const lastCardsRef = useRef<string>("");
  const lastPhaseRef = useRef<string>("");
  const lastResultRef = useRef<string>("");
  const seenChatIds = useRef<Set<string>>(new Set());

  const pushLog = (kind: ChatLine["kind"], text: string) => {
    setLogLines((prev) =>
      [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          kind,
          text,
          at: Date.now(),
        },
      ].slice(-120),
    );
  };

  // Merge remote chat into display (dedupe by id)
  const chatLines: ChatLine[] = (props.chatMessages ?? [])
    .filter((m) => {
      if (seenChatIds.current.has(m.id)) return true;
      seenChatIds.current.add(m.id);
      return true;
    })
    .map((m) => {
      const self = props.selfWallet && m.playerId === props.selfWallet;
      const who = self
        ? "You"
        : m.playerId === "ai:betudontbet" || m.playerId.includes("betudontbet")
          ? "BetUDontBet"
          : m.playerId.startsWith("ai:")
            ? "BetUDontBet"
            : `${m.playerId.slice(0, 4)}…${m.playerId.slice(-4)}`;
      return {
        id: m.id,
        kind: "chat" as const,
        text: `${who}: ${m.text}`,
        at: m.at,
      };
    });

  useEffect(() => {
    if (!props.lastAction) return;
    const key = `${props.lastAction.playerId}:${props.lastAction.action}:${props.lastAction.amount ?? ""}`;
    if (key === lastActRef.current) return;
    lastActRef.current = key;
    const amt =
      props.lastAction.amount != null && props.lastAction.amount > 0
        ? ` ${props.lastAction.amount}`
        : "";
    const pid = props.lastAction.playerId;
    const who =
      props.selfWallet && pid === props.selfWallet
        ? "You"
        : pid.startsWith("ai:")
          ? "BetUDontBet"
          : `${pid.slice(0, 6)}…`;
    pushLog(
      "action",
      `${who} ${String(props.lastAction.action).toUpperCase()}${amt}`,
    );
  }, [props.lastAction, props.selfWallet]);

  useEffect(() => {
    const cards = props.communityCards ?? [];
    if (!cards.length) return;
    const key = cards.map((c) => `${c.rank}${c.suit}`).join("");
    if (key === lastCardsRef.current) return;
    lastCardsRef.current = key;
    pushLog(
      "cards",
      `Board: ${cards.map((c) => `${c.rank}${c.suit[0]?.toUpperCase() ?? ""}`).join(" ")}`,
    );
  }, [props.communityCards]);

  useEffect(() => {
    if (!props.phase || props.phase === lastPhaseRef.current) return;
    lastPhaseRef.current = props.phase;
    if (props.phase === "waiting") return;
    pushLog("system", `— ${props.phase.toUpperCase()} —`);
  }, [props.phase]);

  useEffect(() => {
    if (!props.handResult || props.handResult === lastResultRef.current) return;
    lastResultRef.current = props.handResult;
    pushLog("action", props.handResult);
  }, [props.handResult]);

  const systemLines = logLines.filter((l) => l.kind === "system");
  const displayChat = [...systemLines.slice(0, 1), ...chatLines].slice(-80);
  const displayLog = logLines.filter((l) => l.kind !== "chat");

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayChat.length, displayLog.length, tab]);

  const visible = tab === "chat" ? displayChat : displayLog;

  const onSend = () => {
    const t = input.trim().slice(0, 200);
    if (!t) return;
    // Only WS — server echoes back to all (including us) via chat_message
    props.sendChat?.(t);
    // Vidu S1 BUDB voice↔voice ONLY on HU VS BUDB (not other cash tables)
    if (props.budbVoice) {
      try {
        void import("@/components/budb-avatar-embed").then((m) =>
          m.relayChatToBudbVidu(t),
        );
      } catch {
        /* optional */
      }
    }
    setInput("");
  };

  return (
    <div className="poker-rail-panel poker-chat-log">
      <div className="poker-rail-panel__title">
        <span>Chat · Log</span>
        <span style={{ fontWeight: 500, opacity: 0.6, fontSize: 10 }}>
          {props.connected ? "live" : "local"}
        </span>
      </div>
      <div className="poker-chat-tabs">
        <button
          type="button"
          data-active={tab === "chat"}
          onClick={() => setTab("chat")}
        >
          Chat
        </button>
        <button
          type="button"
          data-active={tab === "log"}
          onClick={() => setTab("log")}
        >
          Log / cards
        </button>
      </div>
      <div className="poker-chat-lines">
        {visible.map((l) => (
          <div key={l.id} className={`poker-chat-line--${l.kind}`}>
            {l.text}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {tab === "chat" && (
        <div className="poker-chat-input-row">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              props.connected ? "Say something…" : "Connect to table for live chat"
            }
            maxLength={200}
            disabled={!props.connected}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSend();
              }
            }}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!props.connected}
            style={{
              background: "rgba(234,179,8,0.2)",
              border: "1px solid rgba(234,179,8,0.4)",
              borderRadius: 6,
              color: "#fbbf24",
              fontSize: 11,
              fontWeight: 700,
              padding: "0 10px",
              cursor: props.connected ? "pointer" : "not-allowed",
              opacity: props.connected ? 1 : 0.5,
            }}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
